// Appointment — the calendar: bookings (status-colored) in Month / Week / Day
// views. Day click = new booking; chip click = view/edit. Drag to reschedule
// (a confirmation with a real slot picker opens on drop).

import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Loader2, Search, Filter, RotateCcw, X } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import {
  appointmentsService, APPT_STATUS_META, to12h, ymd,
  type ApptBooking, type ApptBookingStatus, type ApptCalendarBooking, type ApptService, type ApptSlot,
} from '@/services/appointments.service';
import FilterCheckList, { type CheckOption } from './FilterCheckList';
import BookingDialog from './BookingDialog';
import BookingDetailDialog from './BookingDetailDialog';
import BookingHoverCard from './BookingHoverCard';
import AppointmentTimeGrid from './AppointmentTimeGrid';
import { BookingLinkButton } from './BookingLinkButton';
import { useLabels } from '@/contexts/WorkspaceContext';

type View = 'month' | 'week' | 'day';

const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d: Date) => addDays(d, -d.getDay()); // Sunday
const monthLabel = (d: Date) => d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

const STATUS_DOT: Record<string, string> = {
  PENDING: 'bg-amber-500', CONFIRMED: 'bg-blue-500', COMPLETED: 'bg-emerald-500',
  CANCELLED: 'bg-red-500', NO_SHOW: 'bg-slate-400',
};

const AppointmentCalendar = () => {
  const t = useLabels();
  const [view, setView] = useState<View>('month');
  const [anchor, setAnchor] = useState(new Date());
  const [bookings, setBookings] = useState<ApptCalendarBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState<ApptService[]>([]);

  const [bookingOpen, setBookingOpen] = useState(false);
  const [editBooking, setEditBooking] = useState<ApptBooking | null>(null);
  const [viewBooking, setViewBooking] = useState<ApptBooking | null>(null);
  const [viewOpen, setViewOpen] = useState(false);

  // search + advanced filters. Applied sets = null means "all selected"
  // (the default). Anything narrower flags the filter as active.
  const [query, setQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [svcFilter, setSvcFilter] = useState<Set<string> | null>(null);
  const [statusFilter, setStatusFilter] = useState<Set<ApptBookingStatus> | null>(null);
  const filterActive = svcFilter !== null || statusFilter !== null;

  // draft selections while the dialog is open (Apply commits, Reset = all)
  const ALL_STATUSES = Object.keys(APPT_STATUS_META) as ApptBookingStatus[];
  const [svcDraft, setSvcDraft] = useState<Set<string>>(new Set());
  const [statusDraft, setStatusDraft] = useState<Set<ApptBookingStatus>>(new Set());
  const openFilters = () => {
    const allSvc = new Set(services.map((s) => s.serviceUid));
    setSvcDraft(svcFilter ? new Set(svcFilter) : allSvc);
    setStatusDraft(statusFilter ? new Set(statusFilter) : new Set(ALL_STATUSES));
    setFilterOpen(true);
  };
  const resetFilters = () => {
    setSvcDraft(new Set(services.map((s) => s.serviceUid)));
    setStatusDraft(new Set(ALL_STATUSES));
  };
  const applyFilters = () => {
    setSvcFilter(svcDraft.size === services.length ? null : new Set(svcDraft));
    setStatusFilter(statusDraft.size === ALL_STATUSES.length ? null : new Set(statusDraft));
    setFilterOpen(false);
  };
  const statusOptions: CheckOption[] = ALL_STATUSES.map((st) => ({ value: st, label: APPT_STATUS_META[st].label }));
  const serviceOptions: CheckOption[] = services.map((s) => ({
    value: s.serviceUid, label: s.name,
    subtitle: s.description || `${s.durationMinutes} min`,
    image: s.imageUrl,
  }));
  const appliedFilterCount = (svcFilter ? 1 : 0) + (statusFilter ? 1 : 0);

  const [dialogDate, setDialogDate] = useState<string | undefined>(undefined);

  // month-view drag-and-drop (drag a booking to another day → change date, keep time)
  const [dragBk, setDragBk] = useState<ApptCalendarBooking | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  // visible range per view
  const range = useMemo(() => {
    if (view === 'day') return { from: anchor, to: anchor };
    if (view === 'week') { const s = startOfWeek(anchor); return { from: s, to: addDays(s, 6) }; }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return { from: gridStart, to: addDays(gridStart, 41) };
  }, [view, anchor]);

  const load = () => {
    setLoading(true);
    appointmentsService.calendarFeed(ymd(range.from), ymd(range.to))
      .then((r) => setBookings(r.bookings ?? []))
      .catch(() => { /* interceptor toasts */ })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [range.from.getTime(), range.to.getTime()]);
  useEffect(() => {
    appointmentsService.listServices().then((r) => setServices(r.items ?? [])).catch(() => { /* ignore */ });
  }, []);

  // ── search across ALL dates (server-side) — results list mode ──
  const searching = query.trim().length > 0;
  const [searchResults, setSearchResults] = useState<ApptCalendarBooking[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  useEffect(() => {
    if (!searching) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      setSearchLoading(true);
      appointmentsService.calendarSearch(
        query.trim(),
        statusFilter ? Array.from(statusFilter) : undefined,
        svcFilter ? Array.from(svcFilter) : undefined,
      )
        .then((r) => setSearchResults(r.bookings ?? []))
        .catch(() => { /* ignore */ })
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, svcFilter, statusFilter]);

  // ── drag-to-reschedule: dropping opens a confirmation with a slot picker ──
  const [reschedFor, setReschedFor] = useState<{ b: ApptCalendarBooking; date: string; wantStart: string } | null>(null);
  const [reschedSlots, setReschedSlots] = useState<ApptSlot[]>([]);
  const [reschedSlotLoading, setReschedSlotLoading] = useState(false);
  const [reschedPick, setReschedPick] = useState<ApptSlot | null>(null);
  const [reschedBusy, setReschedBusy] = useState(false);

  const reschedule = (b: ApptCalendarBooking, date: string, start: string) => {
    if (b.date === date && b.start === start) return; // no-op drop
    setReschedFor({ b, date, wantStart: start });
  };
  // load the target day's real slots; preselect the same time if free, else nearest available
  useEffect(() => {
    if (!reschedFor?.b.serviceUid) { setReschedSlots([]); setReschedPick(null); return; }
    let cancelled = false;
    setReschedSlotLoading(true); setReschedPick(null);
    appointmentsService.availableSlots(reschedFor.b.serviceUid, reschedFor.date)
      .then((r) => {
        if (cancelled) return;
        const slots = r.slots ?? [];
        setReschedSlots(slots);
        const free = slots.filter((s) => s.available);
        const exact = free.find((s) => s.start === reschedFor.wantStart);
        if (exact) { setReschedPick(exact); return; }
        // nearest available to the wanted time
        const want = Number(reschedFor.wantStart.slice(0, 2)) * 60 + Number(reschedFor.wantStart.slice(3));
        const near = free.slice().sort((a, b2) => {
          const am = Number(a.start.slice(0, 2)) * 60 + Number(a.start.slice(3));
          const bm = Number(b2.start.slice(0, 2)) * 60 + Number(b2.start.slice(3));
          return Math.abs(am - want) - Math.abs(bm - want);
        })[0];
        setReschedPick(near ?? null);
      })
      .catch(() => { if (!cancelled) setReschedSlots([]); })
      .finally(() => { if (!cancelled) setReschedSlotLoading(false); });
    return () => { cancelled = true; };
  }, [reschedFor]);

  const confirmReschedule = async () => {
    if (!reschedFor || !reschedPick) return;
    const { b, date } = reschedFor;
    setReschedBusy(true);
    try {
      await appointmentsService.updateBooking(b.bookingUid, { bookingDate: date, slotStart: reschedPick.start, slotEnd: reschedPick.end });
      toast.success(`Booking moved — ${b.bookingNo} → ${niceDate(date)} at ${to12h(reschedPick.start)}`);
      load();
    } catch (e: any) {
      // nothing changed — the booking stays where it was
      toast.error(e?.response?.data?.message ?? "Couldn't move this booking — that time isn't available.");
    }
    setReschedBusy(false);
    setReschedFor(null);
  };
  const niceDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  // keyword (name / phone / email / notes / booking no / service) + filters
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bookings.filter((b) => {
      if (svcFilter && !svcFilter.has(b.serviceUid)) return false;
      if (statusFilter && !statusFilter.has(b.status)) return false;
      if (!q) return true;
      return [b.customerName, b.customerPhone, b.customerEmail, b.notes, b.bookingNo, b.serviceName]
        .some((f) => (f || '').toLowerCase().includes(q));
    });
  }, [bookings, query, svcFilter, statusFilter]);

  const itemsByDay = useMemo(() => {
    const m = new Map<string, ApptCalendarBooking[]>();
    filtered.forEach((b) => { if (!m.has(b.date)) m.set(b.date, []); m.get(b.date)!.push(b); });
    m.forEach((v) => v.sort((a, b) => a.start.localeCompare(b.start)));
    return m;
  }, [filtered]);

  const nav = (dir: -1 | 1) => setAnchor((a) =>
    view === 'month' ? new Date(a.getFullYear(), a.getMonth() + dir, 1)
    : addDays(a, view === 'week' ? dir * 7 : dir));

  const openNewBooking = (date?: string) => { setEditBooking(null); setDialogDate(date); setBookingOpen(true); };
  // calendar chip → view-only detail (with an Edit action inside)
  const openBooking = async (bookingUid: string) => {
    try {
      const r = await appointmentsService.getBooking(bookingUid);
      if (r.item) { setViewBooking(r.item); setViewOpen(true); }
    } catch { /* interceptor toasts */ }
  };
  const editFromView = () => {
    if (!viewBooking) return;
    setViewOpen(false);
    setEditBooking(viewBooking);
    setDialogDate(undefined);
    setBookingOpen(true);
  };

  const chip = (b: ApptCalendarBooking) => (
    <BookingHoverCard key={`b${b.bookingUid}`} booking={b}>
      <button
        type="button"
        draggable={b.status !== 'CANCELLED'}
        onDragStart={(ev) => { ev.stopPropagation(); setDragBk(b); }}
        onDragEnd={() => { setDragBk(null); setDragOverKey(null); }}
        onClick={(ev) => { ev.stopPropagation(); void openBooking(b.bookingUid); }}
        className={clsx(
          'w-full text-left rounded-md px-1.5 py-[3px] text-[11px] leading-tight cursor-pointer flex items-center gap-1.5 min-w-0',
          'border border-transparent transition-[border-color,filter] duration-150',
          'hover:brightness-90 hover:border-current',
          APPT_STATUS_META[b.status]?.className ?? 'bg-surface-alt text-text-primary',
          b.status === 'CANCELLED' && 'line-through opacity-70',
        )}
      >
        <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0 mt-[3px] self-start', STATUS_DOT[b.status] ?? 'bg-text-muted')} />
        <span className="min-w-0">
          <span className="block truncate text-[10px] font-bold opacity-70">{b.bookingNo}</span>
          <span className="block truncate font-semibold">{b.serviceName}</span>
          <span className="block truncate opacity-80">{b.customerName || b.customerPhone || '—'}</span>
        </span>
      </button>
    </BookingHoverCard>
  );

  /* ── month grid ── */
  const monthGrid = () => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    const today = ymd(new Date());
    const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    return (
      <div className="grid grid-cols-7 border-l border-t border-border rounded-xl overflow-hidden bg-surface">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide text-text-muted border-r border-b border-border bg-surface-alt/50">{d}</div>
        ))}
        {cells.map((d) => {
          const key = ymd(d);
          const inMonth = d.getMonth() === anchor.getMonth();
          const items = (itemsByDay.get(key) ?? []).map(chip);
          return (
            <div
              key={key}
              onClick={() => openNewBooking(key)}
              onDragOver={(e) => { if (dragBk) { e.preventDefault(); if (dragOverKey !== key) setDragOverKey(key); } }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragBk && dragBk.date !== key) reschedule(dragBk, key, dragBk.start);
                setDragBk(null); setDragOverKey(null);
              }}
              className={clsx(
                'min-h-[104px] border-r border-b border-border p-1.5 cursor-pointer transition-colors flex flex-col gap-1',
                dragOverKey === key ? 'bg-primary-soft ring-1 ring-inset ring-primary/40' : 'hover:bg-surface-alt/40',
                !inMonth && 'bg-surface-alt/25',
              )}
            >
              <span className={clsx(
                'text-[11.5px] font-semibold w-6 h-6 rounded-full flex items-center justify-center',
                key === today ? 'bg-primary text-on-primary' : inMonth ? 'text-text-primary' : 'text-text-muted/60',
              )}>{d.getDate()}</span>
              {items.slice(0, 3)}
              {items.length > 3 && (
                <button type="button" onClick={(e) => { e.stopPropagation(); setAnchor(d); setView('day'); }} className="text-[10.5px] text-primary font-semibold border-none bg-transparent cursor-pointer text-left px-1">
                  +{items.length - 3} more
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Topbar
        title={t('appointments')}
        breadcrumb={`${t('appointments')} / Calendar`}
        description="All your appointment bookings — drag to reschedule."
        right={<BookingLinkButton />}
      />
      <div className="w-full px-7 py-6">
        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="flex items-center gap-1">
            <Button variant="secondary" size="sm" className="!h-8 !w-8 !px-0" onClick={() => nav(-1)}><ChevronLeft size={15} /></Button>
            <Button variant="secondary" size="sm" className="!h-8 !w-8 !px-0" onClick={() => nav(1)}><ChevronRight size={15} /></Button>
            <Button variant="secondary" size="sm" onClick={() => setAnchor(new Date())}>Today</Button>
          </div>
          <span className="text-[15px] font-bold text-text-primary ml-1">
            {view === 'month' ? monthLabel(anchor)
              : view === 'week' ? `${startOfWeek(anchor).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – ${addDays(startOfWeek(anchor), 6).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`
              : anchor.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
          {loading && <Loader2 size={15} className="animate-spin text-text-muted" />}

          <div className="ml-auto flex items-center gap-2">
            {/* search — 🔍 prefix, filter suffix (opens advanced filters) */}
            <div className="flex items-center gap-1.5 bg-surface border border-border rounded-lg pl-2.5 pr-1 h-8 w-[240px]">
              <Search size={14} className="text-text-muted shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, phone, email, notes, no…"
                className="border-none bg-transparent text-[12.5px] flex-1 min-w-0 text-text-primary placeholder:text-text-muted outline-none"
              />
              {query && (
                <button type="button" onClick={() => setQuery('')} className="border-none bg-transparent cursor-pointer text-text-muted hover:text-text-primary p-0.5 shrink-0"><X size={13} /></button>
              )}
              <button type="button" title="Filters" onClick={openFilters} className={clsx(
                'relative w-6 h-6 rounded-md flex items-center justify-center cursor-pointer border-none transition-colors shrink-0',
                filterActive ? 'bg-primary text-on-primary' : 'bg-transparent text-text-muted hover:text-text-primary hover:bg-surface-alt',
              )}>
                <Filter size={13} />
                {filterActive && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-danger ring-2 ring-surface" />
                )}
              </button>
            </div>

            <div className="flex rounded-lg bg-surface-alt p-0.5">
              {(['month', 'week', 'day'] as View[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={clsx(
                    'border-none cursor-pointer text-[12px] font-semibold px-3 py-[5px] rounded-md capitalize transition-colors',
                    view === v ? 'bg-surface text-text-primary shadow-soft' : 'bg-transparent text-text-muted hover:text-text-primary',
                  )}
                >{v}</button>
              ))}
            </div>
            <Button size="sm" iconLeft={<Plus size={15} />} onClick={() => openNewBooking(ymd(anchor))}>New booking</Button>
          </div>
        </div>

        {/* views — search results (all dates) OR the calendar */}
        {searching ? (
          <div className="border border-border rounded-xl overflow-hidden bg-surface">
            <div className="px-4 py-2.5 border-b border-border bg-surface-alt/40 flex items-center gap-2 text-[12.5px]">
              <Search size={14} className="text-text-muted" />
              <span className="font-semibold text-text-primary">Search results</span>
              <span className="text-text-muted">across all dates</span>
              {searchLoading && <Loader2 size={14} className="animate-spin text-text-muted ml-1" />}
              <span className="ml-auto text-text-muted">{searchResults.length} found</span>
            </div>
            {searchResults.length === 0 && !searchLoading ? (
              <div className="py-12 text-center text-[13px] text-text-muted">No bookings match "{query}".</div>
            ) : (
              <div className="divide-y divide-border max-h-[calc(100vh-260px)] overflow-y-auto">
                {searchResults.map((b) => (
                  <button key={b.bookingUid} type="button" onClick={() => void openBooking(b.bookingUid)}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-surface-alt/40 transition-colors border-none bg-transparent cursor-pointer">
                    <span className={clsx('w-2 h-2 rounded-full shrink-0', STATUS_DOT[b.status])} />
                    <span className="w-[92px] shrink-0 text-[12px] font-semibold text-primary">{b.bookingNo}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-semibold text-text-primary truncate">{b.serviceName}</span>
                      <span className="block text-[11.5px] text-text-muted truncate">{b.customerName || b.customerPhone || '—'}</span>
                    </span>
                    <span className="text-[12px] text-text-muted text-right shrink-0">
                      <span className="block">{new Date(`${b.date}T00:00:00`).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      <span className="block">{to12h(b.start)} – {to12h(b.end)}</span>
                    </span>
                    <span className={clsx('text-[10.5px] font-semibold rounded-full px-2 py-0.5 shrink-0', APPT_STATUS_META[b.status]?.className)}>{APPT_STATUS_META[b.status]?.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {view === 'month' && monthGrid()}
            {view === 'week' && (
              <AppointmentTimeGrid
                days={Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i))}
                bookingsByDay={itemsByDay}
                onOpen={openBooking}
                onNewAt={(date) => { setEditBooking(null); setDialogDate(date); setBookingOpen(true); }}
                onReschedule={reschedule}
              />
            )}
            {view === 'day' && (
              <AppointmentTimeGrid
                days={[anchor]}
                bookingsByDay={itemsByDay}
                onOpen={openBooking}
                onNewAt={(date) => { setEditBooking(null); setDialogDate(date); setBookingOpen(true); }}
                onReschedule={reschedule}
              />
            )}
          </>
        )}

        {/* legend */}
        <div className="flex flex-wrap items-center gap-3 mt-3 text-[11.5px] text-text-muted">
          {Object.entries(APPT_STATUS_META).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={clsx('w-2 h-2 rounded-full', STATUS_DOT[k])} /> {v.label}
            </span>
          ))}
        </div>
      </div>

      {/* filter dialog */}
      <Modal open={filterOpen} onClose={() => setFilterOpen(false)} title={`Filters${appliedFilterCount > 0 ? ` (${appliedFilterCount})` : ''}`}>
        <div className="space-y-4 py-1 min-w-0">
          <FilterCheckList
            label="Status"
            options={statusOptions}
            selected={statusDraft}
            onChange={(n) => setStatusDraft(n as Set<ApptBookingStatus>)}
            searchPlaceholder="Search status…"
          />
          <FilterCheckList
            label="Service"
            options={serviceOptions}
            selected={svcDraft}
            onChange={setSvcDraft}
            withImage
            searchPlaceholder="Search service…"
          />
        </div>
        <div className="flex items-center justify-between gap-2 pt-4 mt-2 border-t border-border">
          <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} onClick={resetFilters}>Reset</Button>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setFilterOpen(false)}>Cancel</Button>
            <Button size="sm" iconLeft={<Filter size={14} />} onClick={applyFilters}>Apply</Button>
          </div>
        </div>
      </Modal>

      <BookingDialog
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        onSaved={load}
        services={services}
        booking={editBooking}
        defaultDate={dialogDate}
      />
      <BookingDetailDialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        booking={viewBooking}
        service={services.find((s) => s.serviceUid === viewBooking?.serviceUid) ?? null}
        onEdit={editFromView}
        onChanged={load}
      />

      {/* reschedule confirmation (drag & drop) — pick a real slot on the target day */}
      <Modal open={!!reschedFor} onClose={() => { if (!reschedBusy) setReschedFor(null); }} title="Move this booking?" widthClass="max-w-[460px]">
        {reschedFor && (
          <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-border bg-surface-alt/40 px-4 py-3">
              <div className="text-[12px] font-semibold text-primary">{reschedFor.b.bookingNo}</div>
              <div className="text-[13.5px] font-semibold text-text-primary mt-0.5">{reschedFor.b.serviceName}</div>
              <div className="text-[12px] text-text-muted">{reschedFor.b.customerName || reschedFor.b.customerPhone || '—'}</div>
            </div>
            <div className="flex items-center gap-2 text-[12.5px]">
              <span className="text-text-muted">From</span>
              <span className="font-medium text-text-primary">{niceDate(reschedFor.b.date)} · {to12h(reschedFor.b.start)}</span>
              <ChevronRight size={15} className="text-text-muted" />
              <span className="font-semibold text-primary">{niceDate(reschedFor.date)}</span>
            </div>

            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1.5">Pick a time on {niceDate(reschedFor.date)}</div>
              {reschedSlotLoading ? (
                <div className="flex items-center gap-2 text-[13px] text-text-muted py-3"><Loader2 size={15} className="animate-spin" /> Loading available times…</div>
              ) : reschedSlots.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border text-[12px] text-text-muted px-3 py-4 text-center">
                  No availability on this day — the service is closed or outside its booking window.
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-1.5 max-h-[176px] overflow-y-auto pr-1">
                  {reschedSlots.map((s) => {
                    const sel = reschedPick?.start === s.start;
                    return (
                      <button
                        key={s.start}
                        type="button"
                        disabled={!s.available}
                        onClick={() => setReschedPick(s)}
                        className={clsx(
                          'border rounded-lg px-1 py-[7px] text-[12px] font-medium transition-colors cursor-pointer',
                          sel ? 'bg-primary text-on-primary border-primary shadow-soft'
                            : s.available ? 'bg-surface border-border text-text-primary hover:border-primary/60 hover:bg-primary-soft/40'
                            : 'bg-surface-alt/60 border-border text-text-muted/50 line-through cursor-not-allowed',
                        )}
                      >{to12h(s.start)}</button>
                    );
                  })}
                </div>
              )}
              {!reschedSlotLoading && reschedSlots.length > 0 && reschedPick && reschedPick.start !== reschedFor.wantStart && (
                <p className="text-[11px] text-warning mt-1.5">{to12h(reschedFor.wantStart)} isn't free that day — nearest available time selected.</p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setReschedFor(null)} disabled={reschedBusy}>Cancel</Button>
              <Button onClick={() => void confirmReschedule()} disabled={reschedBusy || !reschedPick} loading={reschedBusy}>
                {reschedPick ? `Move to ${to12h(reschedPick.start)}` : 'Move booking'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AppointmentCalendar;
